<template>
  <v-container>
    <ResourceDialog
      @save="save"
      ref="emailDialog"
      :cancellable="false"
      :closeable="false"
      :fields="FIELDS"
      :max-width="800"
      :processing="processing"
      title="Add your email to continue"
      center-title
    />
  </v-container>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import {
  getRecaptchaEnterpriseToken,
  parentShouldUseRecaptchaEnterpriseToken,
} from '@/shared/services/google_recaptcha_enterprise';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEmailValidation from '@/shared/composables/useEmailValidation';
import useEventBus from '@/shared/composables/useEventBus';

const FIELDS = [
  { text: 'Email', value: 'email' },
  { text: 'Confirm your email', value: 'confirmEmail' },
];

const emit = defineEmits(['error']);

const { api } = useApi();
const { validEmail } = useEmailValidation();
const eventBus = useEventBus();
const router = useRouter();
const store = useStore();

const emailDialog = ref(null);
const processing = ref(false);

onMounted(() => {
  emailDialog.value.open({});
});

function emitError(message) {
  emit('error', message);
  eventBus.chime(message);
}

async function save(newVal) {
  if (!newVal.email) {
    emitError('Please fill out all fields');
    return;
  }

  if (!validEmail(newVal.email)) {
    emitError('Please enter a valid email, including the domain (e.g., user@example.com)');
    return;
  }

  if (!validEmail(newVal.confirmEmail)) {
    emitError('Please enter a valid confirmation email');
    return;
  }

  if (newVal.email !== newVal.confirmEmail) {
    emitError('Emails do not match');
    return;
  }

  const parentConfig = store.state.config;
  const needsRecaptchaToken = parentShouldUseRecaptchaEnterpriseToken(parentConfig);
  const token = needsRecaptchaToken
    ? await getRecaptchaEnterpriseToken('PROFILE_CONTACT_UPDATE', parentConfig?.env)
    : null;

  if (needsRecaptchaToken && !token) {
    emitError('We could not verify your request. Please try again.');
    return;
  }

  processing.value = true;

  try {
    const resp = await api.member.profile.update({
      email: newVal.email,
      token,
    });

    if (!resp?.data) return;

    store.commit('setProfile', null);
    store.commit('setAuthenticated', false);
    await router.replace({
      name: 'CodeSent',
      query: { token: resp.data.code_id, email_sent_to: newVal.email },
    });
  } catch (error) {
    emitError(error);
  } finally {
    processing.value = false;
  }
}
</script>
